import React, { useState, useEffect, useCallback, useMemo } from "react";
import API from "../../../api/api";
import {
  FaClipboardList,
  FaPlus,
  FaCalendarAlt,
  FaUserNinja,
  FaStar,
  FaTrashAlt,
  FaDownload,
  FaPrint,
  FaRedoAlt,
  FaCheckCircle,
  FaSearch,
} from "react-icons/fa";

import Swal from "sweetalert2";
import FileUploadWithScanner from "../../../components/common/FileUploadWithScanner";
import { buildReportTable, outputCampReport } from "./reportOutput";

const CoachDailyReports = () => {
  const [athletes, setAthletes] = useState([]);
  const [reports, setReports] = useState([]);
  const [activeCamp, setActiveCamp] = useState(null);
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [loadingAthletes, setLoadingAthletes] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().slice(0, 10));
  const [selectedAthleteId, setSelectedAthleteId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [sessionFilter, setSessionFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);

  // Form state
  const [form, setForm] = useState({
    athlete_id: "",
    report_date: new Date().toISOString().slice(0, 10),
    training_session: "morning",
    activity_details: "",
    performance_rating: 4,
    fitness_condition: "Good",
    discipline_rating: 4,
    coach_remarks: "",
    file: null,
  });

  const fetchAthletes = useCallback(async () => {
    setLoadingAthletes(true);
    try {
      const res = await API.camp.coachPortal.getMyAthletes();
      const list = res?.data?.athletes || res?.athletes || [];
      setAthletes(list);
      if (list.length > 0 && !form.athlete_id) {
        setForm((prev) => ({ ...prev, athlete_id: list[0].id }));
      }
    } catch (err) {
      console.error("Failed to load coach athletes", err);
    } finally {
      setLoadingAthletes(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchActiveCamp = useCallback(async () => {
    try {
      const res = await API.camp.coachPortal.getActiveCamp();
      const cData = res?.data?.camp || res?.camp || null;
      if (cData) setActiveCamp(cData);
    } catch (e) {
      console.warn("Could not fetch active camp:", e);
    }
  }, []);

  const fetchReports = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedDate) params.date = selectedDate;
      if (selectedAthleteId) params.athlete_id = selectedAthleteId;

      const res = await API.camp.coachPortal.getAthleteDailyReports(params);
      const list = res?.data?.reports || res?.reports || res?.data || [];
      setReports(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to load daily reports", err);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, selectedAthleteId]);

  useEffect(() => {
    fetchAthletes();
    fetchActiveCamp();
  }, [fetchAthletes, fetchActiveCamp]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.athlete_id) {
      Swal.fire("Missing Athlete", "Please choose an athlete for this daily report.", "warning");
      return;
    }

    const selectedAth = athletes.find((a) => String(a.id) === String(form.athlete_id));
    const campId = selectedAth?.camp_id || activeCamp?.id || athletes[0]?.camp_id;

    const formData = new FormData();
    if (campId) {
      formData.append("camp_id", campId);
    }
    formData.append("athlete_id", form.athlete_id);
    formData.append("report_date", form.report_date);
    formData.append("training_session", form.training_session);
    formData.append("activity_details", form.activity_details || "");
    formData.append("performance_rating", form.performance_rating);
    formData.append("fitness_condition", form.fitness_condition);
    formData.append("discipline_rating", form.discipline_rating);
    formData.append("coach_remarks", form.coach_remarks || "");
    if (form.file) {
      formData.append("file", form.file);
    }

    setSubmitting(true);
    try {
      const res = await API.camp.coachPortal.saveAthleteDailyReport(formData);
      Swal.fire({
        icon: "success",
        title: "Report Saved",
        text: res?.data?.message || "Daily athlete training evaluation recorded successfully.",
        timer: 1800,
        showConfirmButton: false,
      });
      setShowModal(false);
      setForm((prev) => ({
        ...prev,
        activity_details: "",
        coach_remarks: "",
        file: null,
      }));
      fetchReports();
    } catch (err) {
      console.error("Failed to save report:", err);
      const serverMsg = err?.response?.data?.message || 
        (err?.response?.data?.errors ? Object.values(err.response.data.errors).flat().join(", ") : null) || 
        "Failed to record daily report.";
      Swal.fire("Error", serverMsg, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Delete Report?",
      text: "Are you sure you want to delete this daily evaluation record?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      confirmButtonColor: "#ef4444",
    });

    if (confirm.isConfirmed) {
      try {
        await API.camp.coachPortal.deleteAthleteDailyReport(id);
        Swal.fire("Deleted", "Daily report removed.", "success");
        fetchReports();
      } catch (err) {
        Swal.fire("Error", "Could not delete report.", "error");
      }
    }
  };

  const filteredReports = useMemo(() => {
    return reports.filter((rep) => {
      if (sessionFilter !== "all" && rep.training_session !== sessionFilter) {
        return false;
      }
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase().trim();
      const athName = (rep.athlete?.name || rep.athlete_name || "").toLowerCase();
      const bib = String(rep.athlete?.bib_no || "").toLowerCase();
      const drills = (rep.activity_details || "").toLowerCase();
      const remarks = (rep.coach_remarks || "").toLowerCase();
      const session = (rep.training_session || "").toLowerCase();
      return (
        athName.includes(term) ||
        bib.includes(term) ||
        drills.includes(term) ||
        remarks.includes(term) ||
        session.includes(term)
      );
    });
  }, [reports, sessionFilter, searchTerm]);

  const outputDailyWorkouts = (action) => outputCampReport({
    title: "Daily Workouts Report",
    action,
    body: buildReportTable({
      heading: "Daily Workouts and Athlete Performance Report",
      subtitle: `Date: ${selectedDate || "All dates"} | ${filteredReports.length} records`,
      columns: [
        { key: "serial", label: "#" },
        { key: "athlete", label: "Athlete" },
        { key: "bib", label: "BIB" },
        { key: "session", label: "Session" },
        { key: "activities", label: "Workout / Drills" },
        { key: "performance", label: "Performance" },
        { key: "fitness", label: "Fitness" },
        { key: "discipline", label: "Discipline" },
        { key: "remarks", label: "Coach Remarks" }
      ],
      rows: filteredReports.map((report, index) => ({
        serial: index + 1,
        athlete: report.athlete?.name || report.athlete_name,
        bib: report.athlete?.bib_no || report.bib_no,
        session: report.training_session,
        activities: report.activity_details,
        performance: report.performance_rating,
        fitness: report.fitness_condition,
        discipline: report.discipline_rating,
        remarks: report.coach_remarks
      }))
    })
  });

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <section className="bg-gradient-to-r from-emerald-800 via-teal-800 to-cyan-900 rounded-3xl p-6 text-white shadow-lg flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <span className="text-[10px] uppercase font-bold tracking-widest bg-white/20 px-3 py-1 rounded-full text-emerald-100">
            Training &amp; Progress Monitoring
          </span>
          <h1 className="text-2xl font-black mt-2 tracking-tight flex items-center gap-2.5">
            <FaClipboardList className="text-emerald-300" /> Daily Individual Athlete Reports
          </h1>
          <p className="text-cyan-100 text-xs mt-1 max-w-2xl">
            Record training session drills, fitness condition, discipline, performance rating, and scan attachments for each squad athlete daily.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => outputDailyWorkouts("print")} disabled={!filteredReports.length} className="inline-flex items-center gap-2 px-4 py-3 bg-white/15 hover:bg-white/25 text-white rounded-2xl font-black text-xs disabled:opacity-50"><FaPrint /> Print</button>
          <button type="button" onClick={() => outputDailyWorkouts("download")} disabled={!filteredReports.length} className="inline-flex items-center gap-2 px-4 py-3 bg-cyan-700 hover:bg-cyan-600 text-white rounded-2xl font-black text-xs disabled:opacity-50"><FaDownload /> Download PDF</button>
          <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 px-5 py-3 bg-white text-emerald-900 hover:bg-emerald-50 rounded-2xl font-black text-xs shadow-md transition-all self-start md:self-auto"
        >
          <FaPlus /> New Daily Report
          </button>
        </div>
      </section>

      {/* Filter Bar */}
      <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm flex flex-col sm:flex-row gap-3 items-center justify-between">
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto flex-1">
          {/* Search Input */}
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <FaSearch className="absolute left-3 top-3 text-slate-400 text-xs" />
            <input
              type="text"
              placeholder="Search athlete, drill, remarks..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-3 py-2 bg-slate-50 border border-slate-300 rounded-xl text-xs font-semibold text-slate-800 placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:bg-white"
            />
          </div>

          {/* Date Filter */}
          <div className="flex items-center gap-2">
            <FaCalendarAlt className="text-slate-400 text-xs" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500"
            />
            {selectedDate && (
              <button
                onClick={() => setSelectedDate("")}
                className="text-[10px] text-slate-500 hover:text-slate-800 font-bold underline"
              >
                All Dates
              </button>
            )}
          </div>

          {/* Athlete Filter */}
          <div className="flex items-center gap-2">
            <FaUserNinja className="text-slate-400 text-xs" />
            <select
              value={selectedAthleteId}
              onChange={(e) => setSelectedAthleteId(e.target.value)}
              className="bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">All Squad Athletes ({athletes.length})</option>
              {athletes.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} ({a.bib_no || "No Bib"})
                </option>
              ))}
            </select>
          </div>

          {/* Session Filter */}
          <div>
            <select
              value={sessionFilter}
              onChange={(e) => setSessionFilter(e.target.value)}
              className="bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">All Sessions</option>
              <option value="morning">Morning Session</option>
              <option value="evening">Evening Session</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-2 self-end sm:self-auto">
          {(searchTerm || sessionFilter !== "all" || selectedAthleteId || selectedDate) && (
            <button
              type="button"
              onClick={() => {
                setSearchTerm("");
                setSessionFilter("all");
                setSelectedAthleteId("");
                setSelectedDate("");
              }}
              className="text-xs font-bold text-slate-500 hover:text-rose-600 px-2 py-1 transition"
            >
              Clear
            </button>
          )}

          <button
            onClick={fetchReports}
            disabled={loading}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-emerald-700 bg-slate-100 hover:bg-slate-200 px-3.5 py-2 rounded-xl transition"
          >
            <FaRedoAlt className={loading ? "animate-spin" : ""} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Reports Table */}
      <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
          <h3 className="text-xs font-bold text-slate-800">
            Recorded Daily Reports ({filteredReports.length} of {reports.length})
          </h3>
          <span className="text-[11px] text-slate-500 font-medium">
            Daily training records for coaching camp
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs border-collapse">
            <thead>
              <tr className="bg-slate-100 text-slate-700 font-bold uppercase text-[10px] border-b border-slate-200">
                <th className="py-3 px-3 w-10 text-center">#</th>
                <th className="py-3 px-4">Date &amp; Session</th>
                <th className="py-3 px-4">Athlete</th>
                <th className="py-3 px-4">Training Drills &amp; Activity</th>
                <th className="py-3 px-3 text-center">Fitness</th>
                <th className="py-3 px-3 text-center">Performance</th>
                <th className="py-3 px-3 text-center">Discipline</th>
                <th className="py-3 px-4">Coach Remarks / File</th>
                <th className="py-3 px-3 text-center w-20">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan="9" className="py-12 text-center text-slate-400">
                    Loading daily individual athlete reports...
                  </td>
                </tr>
              ) : filteredReports.length === 0 ? (
                <tr>
                  <td colSpan="9" className="py-12 text-center text-slate-400">
                    {reports.length === 0
                      ? "No daily reports recorded for the selected criteria. Click \"New Daily Report\" to log one."
                      : "No daily reports match your filter criteria."}
                  </td>
                </tr>
              ) : (
                filteredReports.map((rep, idx) => {
                  const fileUrl = rep.file_path ? `${API.BASE_URL?.replace(/\/api$/, "") || ""}/storage/${rep.file_path}` : null;
                  return (
                    <tr key={rep.id} className="hover:bg-slate-50 transition">
                      <td className="py-3 px-3 text-center text-slate-400 font-mono text-[11px]">
                        {idx + 1}
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-bold text-slate-900 font-mono">
                          {rep.report_date}
                        </div>
                        <span className={`inline-block px-2 py-0.5 rounded text-[9px] font-extrabold uppercase mt-0.5 ${
                          rep.training_session === "morning"
                            ? "bg-amber-100 text-amber-800"
                            : rep.training_session === "evening"
                              ? "bg-indigo-100 text-indigo-800"
                              : "bg-teal-100 text-teal-800"
                        }`}>
                          {rep.training_session}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="font-bold text-slate-900">{rep.athlete?.name || rep.athlete_name || "Athlete"}</div>
                        <div className="text-[10px] text-slate-500 font-mono">
                          Bib: {rep.athlete?.bib_no || "—"} &bull; {rep.athlete?.discipline_name || "Squad"}
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <p className="text-xs text-slate-700 max-w-sm line-clamp-2">
                          {rep.activity_details || "General tactical and physical training drills."}
                        </p>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <span className="inline-block px-2 py-0.5 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full font-bold text-[10px]">
                          {rep.fitness_condition || "Fit"}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <div className="flex items-center justify-center gap-1 font-bold text-amber-600">
                          <FaStar className="text-xs" />
                          <span>{rep.performance_rating || "—"}/5</span>
                        </div>
                      </td>
                      <td className="py-3 px-3 text-center">
                        <div className="flex items-center justify-center gap-1 font-bold text-cyan-700">
                          <FaCheckCircle className="text-xs" />
                          <span>{rep.discipline_rating || "—"}/5</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-[11px] text-slate-600 max-w-xs truncate">
                          {rep.coach_remarks || "—"}
                        </div>
                        {fileUrl && (
                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 hover:underline mt-1"
                          >
                            <FaDownload /> Attached Doc
                          </a>
                        )}
                      </td>
                      <td className="py-3 px-3 text-center">
                        <button
                          onClick={() => handleDelete(rep.id)}
                          title="Delete Report"
                          className="p-1.5 text-slate-400 hover:text-red-600 rounded-lg transition"
                        >
                          <FaTrashAlt className="text-xs" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* New Report Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="bg-gradient-to-r from-emerald-800 to-cyan-900 text-white p-5 flex justify-between items-center">
              <div>
                <h3 className="text-base font-black">Record Daily Athlete Evaluation</h3>
                <p className="text-xs text-emerald-100">Log training drills and fitness metrics</p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-white/80 hover:text-white p-1 rounded-lg"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 text-xs">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Select Athlete
                  </label>
                  <select
                    value={form.athlete_id}
                    onChange={(e) => setForm((prev) => ({ ...prev, athlete_id: e.target.value }))}
                    required
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="">Select Athlete</option>
                    {athletes.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name} ({a.bib_no || "No Bib"})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Report Date
                  </label>
                  <input
                    type="date"
                    value={form.report_date}
                    onChange={(e) => setForm((prev) => ({ ...prev, report_date: e.target.value }))}
                    required
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Training Session
                  </label>
                  <select
                    value={form.training_session}
                    onChange={(e) => setForm((prev) => ({ ...prev, training_session: e.target.value }))}
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="morning">Morning Session</option>
                    <option value="evening">Evening Session</option>
                    <option value="full_day">Full Day Combined</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Fitness Condition
                  </label>
                  <select
                    value={form.fitness_condition}
                    onChange={(e) => setForm((prev) => ({ ...prev, fitness_condition: e.target.value }))}
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="Excellent">Excellent</option>
                    <option value="Good">Good</option>
                    <option value="Average">Average</option>
                    <option value="Fatigued">Fatigued</option>
                    <option value="Minor Injury / Recovery">Minor Injury / Recovery</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Performance Score (1 - 5)
                  </label>
                  <select
                    value={form.performance_rating}
                    onChange={(e) => setForm((prev) => ({ ...prev, performance_rating: Number(e.target.value) }))}
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value={5}>5 - Outstanding</option>
                    <option value={4}>4 - Very Good</option>
                    <option value={3}>3 - Satisfactory</option>
                    <option value={2}>2 - Needs Improvement</option>
                    <option value={1}>1 - Below Par</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Discipline &amp; Focus (1 - 5)
                  </label>
                  <select
                    value={form.discipline_rating}
                    onChange={(e) => setForm((prev) => ({ ...prev, discipline_rating: Number(e.target.value) }))}
                    className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs font-bold text-slate-800 focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value={5}>5 - Highly Disciplined</option>
                    <option value={4}>4 - Good</option>
                    <option value={3}>3 - Fair</option>
                    <option value={2}>2 - Inattentive</option>
                    <option value={1}>1 - Indiscipline Warning</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Activities / Drills Performed
                </label>
                <textarea
                  rows={3}
                  value={form.activity_details}
                  onChange={(e) => setForm((prev) => ({ ...prev, activity_details: e.target.value }))}
                  placeholder="e.g. 5km endurance running, sprint starts, high-intensity strength conditioning..."
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl p-3 text-xs text-slate-800 focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Coach Remarks &amp; Feedback
                </label>
                <input
                  type="text"
                  value={form.coach_remarks}
                  onChange={(e) => setForm((prev) => ({ ...prev, coach_remarks: e.target.value }))}
                  placeholder="e.g. Improved reaction time by 0.3s, needs focus on posture."
                  className="w-full bg-slate-50 border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-800 focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div>
                <FileUploadWithScanner
                  label="Attachment / Scanned Drill Sheet (Optional)"
                  name="file"
                  accept=".pdf,image/*"
                  value={form.file}
                  onChange={(file) => setForm((prev) => ({ ...prev, file }))}
                  scannerTitle="Scan Drill Sheet"
                  targetFileName={`drill_sheet_${Date.now()}.jpg`}
                />
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-sm disabled:opacity-50"
                >
                  {submitting ? "Saving..." : "Save Daily Report"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoachDailyReports;
